import merge from 'deepmerge';
import clone from 'lodash/cloneDeep';
import { uniqueId } from 'lodash';
import { JUMP_TYPES } from './AdvWorkflowHelpers';
import {
  ENDPOINT_URL_MAP,
  SEED_DATA_EXCEPTION_MESSAGE,
  UPDATE_ENDPOINT_URL_MAP,
} from './AdvWorkflowConstants';
import { refetchCurrentClient } from '../../AdviserArea/AdviserAreaActions';

const action = name => `ADV.WORKFLOW.${name}`;

export const INITIALIZE_SECTION = action('INITIALIZE_SECTION');
export const EXTEND_TREE = action('EXTEND_TREE');
export const JUMP = action('JUMP');
export const UPDATE_DATA = action('UPDATE_DATA');
export const UPDATE_SUPPORT_DATA = action('UPDATE_SUPPORT_DATA');
export const UPDATE_VALIDATION = action('UPDATE_VALIDATION');
export const SET_DATA = action('SET_DATA');
export const SET_POPULATED = action('SET_POPULATED');
export const SET_BACKUP_DATA = action('SET_BACKUP_DATA');
export const UPDATE_BACKUP_DATA = action('UPDATE_BACKUP_DATA');
export const DO_BACKUP = action('DO_BACKUP');
export const CLEAR_WORKFLOW = action('CLEAR_WORKFLOW');
export const ACQUIRE_LOADING = action('ACQUIRE_LOADING');
export const RELEASE_LOADING = action('RELEASE_LOADING');
export const RANDOM_ACCESS_JUMP = action('RANDOM_ACCESS_JUMP');
export const DEPTH_FIRST_JUMP = action('DEPTH_FIRST_JUMP');
export const UPDATE_GRAPH_DATA_AND_SEQUENTIAL_ADVANCE = action(
  'UPDATE_GRAPH_DATA_AND_SEQUENTIAL_ADVANCE',
);
export const UPDATE_SUPPORT_DATA_AND_SEQUENTIAL_ADVANCE = action(
  'UPDATE_SUPPORT_DATA_AND_SEQUENTIAL_ADVANCE',
);
export const PUSH_ITERATOR_TO_HISTORY = action('PUSH_ITERATOR_TO_HISTORY');
export const POP_ITERATOR_FROM_HISTORY = action('POP_ITERATOR_FROM_HISTORY');
export const BUMP_PROGRESS_BAR = action('BUMP_PROGRESS_BAR');
export const JUMP_BACK_IN_HISTORY = action('JUMP_BACK_IN_HISTORY');
export const SET_ALPHA = action('SET_ALPHA');

export const bumpProgressBar = workflowId => ({
  type: BUMP_PROGRESS_BAR,
  workflowId,
});

export const setAlpha = (workflowId, alpha) => ({
  type: SET_ALPHA,
  workflowId,
  alpha,
});

export const updateBackupData = (workflowId, backupData) => ({
  type: UPDATE_BACKUP_DATA,
  workflowId,
  backupData,
});

export const setPopulated = (workflowId, clientId, populated) => ({
  type: SET_POPULATED,
  workflowId,
  clientId,
  populated,
});

export const acquireLoading = (workflowId, clientId) => ({
  type: ACQUIRE_LOADING,
  workflowId,
  clientId,
});

export const releaseLoading = (workflowId, clientId) => ({
  type: RELEASE_LOADING,
  workflowId,
  clientId,
});

export const save = (
  key,
  onSuccess,
  onFailure,
  workflowId,
  clientId,
  isLastStep,
  onPostProcess,
  onPostSave,
) => (dispatch, getState) => {
  const state = getState();

  const isAdviserArea = !!state.AdviserArea;

  const workflowSubdivision = state.AdvWorkflow[workflowId][clientId];

  let data = {
    [key]: clone(workflowSubdivision.data[key]),
  };
  if (typeof onPostProcess === 'function') {
    data = onPostProcess(data);
  }
  const rawData = data;
  data = JSON.stringify(data);

  const { ADV } = window;

  dispatch(acquireLoading(workflowId, clientId));
  ADV.util
    .request(
      {
        uri: `${UPDATE_ENDPOINT_URL_MAP[workflowId]}?workflowIsLastStep=${isLastStep}`,
        data: {
          clientId,
          json: data,
          paths: [key],
          workflowId,
          workflowStep: JSON.stringify(workflowSubdivision.iterator),
          workflowIsLastStep: isLastStep,
        },
        type: 'POST',
        showSpinner: false,
      },
      true,
    )
    .then(response => {
      const { success, errors } = response;
      if (success) {
        dispatch({
          type: SET_BACKUP_DATA,
          backupData: merge(workflowSubdivision.data, rawData, {
            arrayMerge: (a, b) => b,
          }),
          workflowId,
          clientId,
        });

        if (isAdviserArea) {
          const { isDemo } = state.AdviserArea;

          dispatch(refetchCurrentClient(clientId, isDemo));
        }

        onSuccess(response);
        if (typeof onPostSave === 'function') {
          onPostSave.call(undefined, rawData);
        }
      } else {
        onFailure(errors);
      }
      dispatch(releaseLoading(workflowId, clientId));
    })
    .catch(reason => {
      dispatch(releaseLoading(workflowId, clientId));
      throw reason;
    });
};

export const pushIteratorToHistoryStack = (workflowId, clientId) => ({
  type: PUSH_ITERATOR_TO_HISTORY,
  workflowId,
  clientId,
});

export const popIteratorFromHistoryStack = (workflowId, clientId) => ({
  type: POP_ITERATOR_FROM_HISTORY,
  workflowId,
  clientId,
});

export const revert = (workflowId, clientId) => (dispatch, getState) => {
  const workflowSubdivision = getState().AdvWorkflow[workflowId][clientId];
  dispatch({
    type: SET_DATA,
    data: workflowSubdivision.backupData,
    workflowId,
    clientId,
  });
};

export const updateGraphDataAndSequentialAdvance = (workflowId, path, value, offset) => ({
  type: UPDATE_GRAPH_DATA_AND_SEQUENTIAL_ADVANCE,
  path,
  value,
  workflowId,
  offset,
});
export const updateSupportDataAndSequentialAdvance = (workflowId, path, value, offset) => ({
  type: UPDATE_SUPPORT_DATA_AND_SEQUENTIAL_ADVANCE,
  path,
  value,
  workflowId,
  offset,
});
export const updateData = (data, workflowId) => ({
  type: UPDATE_DATA,
  data,
  workflowId,
});
export const updateSupportData = (data, workflowId) => ({
  type: UPDATE_SUPPORT_DATA,
  data,
  workflowId,
});

export const updateValidation = (validation, workflowId) => ({
  type: UPDATE_VALIDATION,
  validation,
  workflowId,
});

export const randomAccessJump = (workflowId, clientId, iterator) => ({
  type: RANDOM_ACCESS_JUMP,
  workflowId,
  clientId,
  iterator,
});

export const depthFirstJump = (workflowId, nextIterators) => ({
  type: DEPTH_FIRST_JUMP,
  workflowId,
  nextIterators,
});

export const jump = (offset, workflowId) => ({
  type: JUMP,
  offset,
  workflowId,
});

export const jumpBackInHistory = workflowId => ({
  type: JUMP_BACK_IN_HISTORY,
  workflowId,
});

export const skip = (isGoingForward, isGoingBackward, workflowId, clientId) => dispatch => {
  if (isGoingForward) {
    dispatch(jump(JUMP_TYPES.FORWARD, workflowId, clientId));
  } else if (isGoingBackward) {
    dispatch(jump(JUMP_TYPES.BACKWARD, workflowId, clientId));
  }
};

export const initializeSection = (workflowId, clientId) => ({
  type: INITIALIZE_SECTION,
  workflowId,
  clientId,
});

export const resetWorkflow = selectedGoalId => ({
  type: CLEAR_WORKFLOW,
  selectedGoalId,
});

export const extendTree = (branch, workflowId, clientId) => (dispatch, getState) => {
  const workflow = getState().AdvWorkflow[workflowId][clientId];
  let node = workflow.tree;
  for (let i = 0; i < branch.length; i += 1) {
    node = node[branch[i]];
    if (!node) {
      dispatch({
        type: EXTEND_TREE,
        branch,
        workflowId,
        clientId,
      });
      return;
    }
  }
};

export const fetchData = (
  workflowId,
  clientId,
  pathsOptionalArg,
  showSpinner = false,
) => dispatch => {
  const PATHS = {};
  const pathsOptional = pathsOptionalArg || [];
  const fullRefresh = !pathsOptional.length;

  pathsOptional.forEach((pathOptional, index) => {
    PATHS[`paths[${index}]`] = pathOptional;
  });

  if (!ENDPOINT_URL_MAP[workflowId]) {
    throw Error('The workflow seed URL is missing.');
  }
  const { ADV } = window;
  dispatch(acquireLoading(workflowId, clientId));
  if (fullRefresh) {
    dispatch(setPopulated(workflowId, clientId, false));
  }
  ADV.util
    .request(
      {
        uri: ENDPOINT_URL_MAP[workflowId],
        data: {
          clientId,
          ...PATHS,
        },
        type: 'GET',
        showSpinner,
      },
      true,
    )
    .then(({ success, payload }) => {
      const enhancedPayload = {
        ...payload,
        viewData: {
          ...payload.viewData,
          Accounts: (payload.viewData.Accounts || []).map(account => ({
            ...account,
            Contributions: (account.Contributions ?? []).map(contribution => ({
              ...contribution,
              Id: uniqueId(),
              EmployerMultiTierMatch: contribution.EmployerMultiTierMatch
                ? contribution.EmployerMultiTierMatch.map(tier => ({ ...tier, Id: uniqueId() }))
                : null,
            })),
          })),
        },
      };

      if (success) {
        if (fullRefresh) {
          dispatch({
            type: SET_DATA,
            data: enhancedPayload.viewData,
            fullRefresh,
            workflowId,
            clientId,
          });
          dispatch({
            type: SET_BACKUP_DATA,
            backupData: enhancedPayload.viewData,
            workflowId,
            clientId,
          });
        } else {
          const data = {};
          pathsOptional.forEach(pathOptional => {
            data[pathOptional] = enhancedPayload.viewData[pathOptional];
          });
          dispatch({
            type: UPDATE_DATA,
            data,
            workflowId,
            clientId,
          });
          dispatch({
            type: UPDATE_BACKUP_DATA,
            workflowId,
            backupData: data,
          });
        }
      } else {
        throw SEED_DATA_EXCEPTION_MESSAGE;
      }
      dispatch(releaseLoading(workflowId, clientId));
    })
    .catch(reason => {
      dispatch(releaseLoading(workflowId, clientId));
      throw reason;
    });
};
